import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SEOMetaTags } from '../../components/shared/SEOMetaTags';
import { useToast } from '../../hooks/useToast';
import { PublicLayout } from '../../components/layout/PublicLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../../components/ui/card';
import { CheckCircle, Copy, Mail } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { sendOrderConfirmationEmail } from '../../services/shopEmailService';
import { supabase } from '../../lib/supabase';
import { useTenantContext } from '../../context/TenantContext';

// Mapping d'icônes pour personnalisation - Utilise toutes les icônes Lucide
const iconMap = LucideIcons as Record<string, React.ElementType>;

function hexToRgba(hex: string, alpha = 1) {
  const cleaned = hex.replace('#', '');
  const bigint = parseInt(cleaned.length === 3
    ? cleaned.split('').map((c) => c + c).join('')
    : cleaned, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const fadeIn = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } } };

const OrderConfirmationPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { tenant, previewConfig } = useTenantContext();

  // Thème Boutique (preview > tenant)
  const shopTheme = React.useMemo(() => {
    const base = (previewConfig?.theme?.notFound) ?? (tenant?.config?.theme?.notFound) ?? ({} as any);
    const themeColors = {
      c1: (tenant?.config?.color1 as string) || '#fe8d17', // primaire (bouton)
      c2: (tenant?.config?.color2 as string) || '#008aef', // secondaire (bouton)
      c3: (tenant?.config?.color3 as string) || '#ffffff',
      c4: (tenant?.config?.color4 as string) || '#9ca3af',
    };
    const defaults = {
      overlay: {
        enabled: true,
        useGradient: true,
        orientation: 'to-b', // to-b, to-t, to-r, to-l, to-tr, to-br, to-tl, to-bl
        color1: '#08062b',
        color2: '#08062b',
        color3: '#08062b',
        opacity1: 0.95,
        opacity2: 0.80,
        opacity3: 0.95,
      },
      icons: [
        { enabled: true, icon: 'CheckCircle', color: themeColors.c1, size: 32 }, // top-left
        { enabled: true, icon: 'Sparkles',   color: themeColors.c2, size: 28 }, // top-right
        { enabled: true, icon: 'Diamond',    color: themeColors.c2, size: 24 }, // bottom-left
        { enabled: true, icon: 'Star',       color: themeColors.c1, size: 30 }, // bottom-right
      ]
    } as any;
    return {
      overlay: {
        enabled: base.overlay?.enabled ?? defaults.overlay.enabled,
        useGradient: base.overlay?.useGradient ?? defaults.overlay.useGradient,
        orientation: base.overlay?.orientation ?? defaults.overlay.orientation,
        color1: base.overlay?.color1 ?? defaults.overlay.color1,
        color2: base.overlay?.color2 ?? defaults.overlay.color2,
        color3: base.overlay?.color3 ?? defaults.overlay.color3,
        opacity1: base.overlay?.opacity1 ?? defaults.overlay.opacity1,
        opacity2: base.overlay?.opacity2 ?? defaults.overlay.opacity2,
        opacity3: base.overlay?.opacity3 ?? defaults.overlay.opacity3,
      },
      icons: (base.icons && Array.isArray(base.icons) && base.icons.length === 4)
        ? base.icons.map((i: any, idx: number) => ({
            enabled: i?.enabled ?? defaults.icons[idx].enabled,
            icon: i?.icon ?? defaults.icons[idx].icon,
            color: i?.color ?? defaults.icons[idx].color,
            size: Number(i?.size ?? defaults.icons[idx].size),
          }))
        : defaults.icons,
    };
  }, [tenant, previewConfig]);
  
  const [orderCode, setOrderCode] = useState<string>('');
  const [customerEmail, setCustomerEmail] = useState<string>('');
  const [isSendingEmail, setIsSendingEmail] = useState<boolean>(false);
  const [emailSent, setEmailSent] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [orderExists, setOrderExists] = useState<boolean>(false);
  
  // Récupérer le paramètre de l'URL avec useParams
  const { orderCode: urlOrderCode } = useParams<{ orderCode: string }>();
  
  // Configuration SEO personnalisée par tenant (DOIT être avant tous les returns conditionnels)
  const seoConfig = React.useMemo(() => {
    const seo = tenant?.config?.seo || {};
    const currentDomain = window.location.hostname;
    
    return {
      title: `Confirmation de commande - ${tenant?.name || 'Boutique'}`,
      description: `Votre commande a été confirmée avec succès sur ${tenant?.name || 'notre boutique'}.`,
      image: seo.ogImage || tenant?.logo || tenant?.icon,
      url: `https://${currentDomain}/boutique/confirmation`
    };
  }, [tenant]);
  
  useEffect(() => {
    // Récupérer le code de commande depuis l'URL ou localStorage
    const storedOrderCode = localStorage.getItem('last_order_code');
    
    let resolvedCode: string | null = null;
    if (urlOrderCode) {
      resolvedCode = urlOrderCode;
      localStorage.setItem('last_order_code', urlOrderCode);
    } else if (storedOrderCode) {
      resolvedCode = storedOrderCode;
    }
    
    // Vérifier si l'email a déjà été envoyé pour cette commande
    const emailSentKey = `email_sent_${resolvedCode}`;
    const alreadySent = localStorage.getItem(emailSentKey);
    
    if (resolvedCode && !alreadySent) {
      console.log('[OrderConfirmationPage] Vérification si l\'email a déjà été envoyé pour la commande:', resolvedCode);
      
      // Marquer l'email comme envoyé immédiatement pour éviter les envois multiples
      localStorage.setItem(emailSentKey, 'true');
      
      // Récupérer l'ID de commande stocké lors de la vérification
      const orderId = localStorage.getItem('current_order_id') || resolvedCode;
      
      // Envoyer l'email une seule fois
      sendOrderConfirmationEmail(orderId)
        .then(result => {
          if (result.success) {
            console.log('[OrderConfirmationPage] Email de confirmation envoyé automatiquement avec succès');
            setEmailSent(true);
          } else {
            console.error('[OrderConfirmationPage] Erreur lors de l\'envoi automatique de l\'email:', result.error);
            // En cas d'erreur, on retire le marqueur pour permettre une nouvelle tentative
            localStorage.removeItem(emailSentKey);
          }
        })
        .catch(err => {
          console.error('[OrderConfirmationPage] Exception lors de l\'envoi automatique de l\'email:', err);
          // En cas d'erreur, on retire le marqueur pour permettre une nouvelle tentative
          localStorage.removeItem(emailSentKey);
        });
    } else if (alreadySent) {
      console.log('[OrderConfirmationPage] Email déjà envoyé pour cette commande, pas de nouvel envoi');
      setEmailSent(true);
    }

    if (!resolvedCode) {
      toast({
        title: 'Aucune commande trouvée',
        description: 'Impossible de trouver les détails de votre commande',
        variant: 'destructive'
      });
      navigate('/boutique');
      return;
    }

    // Vérifier l'existence de la commande en base pour éviter une page blanche
    const verifyOrder = async () => {
      console.log(`[OrderConfirmation] Début de la vérification pour le code: ${resolvedCode}`);
      try {
        // Vérifier si c'est un ID numérique
        if (!isNaN(Number(resolvedCode))) {
          console.log(`[OrderConfirmation] Recherche par ID numérique: ${Number(resolvedCode)}`);
          const { data, error } = await supabase
            .from('shop_orders')
            .select('id, order_number')
            .eq('id', Number(resolvedCode))
            .maybeSingle();
          
          console.log(`[OrderConfirmation] Résultat recherche par ID:`, data, error);
          
          if (data) {
            setOrderExists(true);
            // Utiliser le numéro de commande (order_number) au lieu de l'ID
            setOrderCode(data.order_number);
            // Stocker également l'ID pour l'envoi d'email
            localStorage.setItem('current_order_id', data.id.toString());
            setIsLoading(false);
            return;
          }
          
          if (error) throw error;
        }
        
        // Si ce n'est pas un ID numérique ou si aucun résultat n'a été trouvé
        console.log(`[OrderConfirmation] Aucune correspondance trouvée pour: ${resolvedCode}`);
        
        // Rediriger vers la boutique après un court délai
        setTimeout(() => {
          toast({
            title: 'Commande introuvable',
            description: 'Impossible de trouver les détails de votre commande',
            variant: 'destructive'
          });
          navigate('/boutique');
        }, 1000);

      } catch (error) {
        console.error('Erreur lors de la vérification de la commande:', error);
        toast({
          title: 'Erreur',
          description: 'Une erreur est survenue lors de la vérification de votre commande',
          variant: 'destructive'
        });
        navigate('/boutique');
      } finally {
        setIsLoading(false);
      }
    };

    verifyOrder();
    
    // Récupérer l'email du client depuis localStorage
    const customerData = localStorage.getItem('checkout_customer_data');
    if (customerData) {
      try {
        const parsedData = JSON.parse(customerData);
        setCustomerEmail(parsedData.email || '');
      } catch (error) {
        console.error('Erreur lors de la récupération des données client:', error);
      }
    }
  }, [location.search, navigate, toast]);
  
  const copyOrderCodeToClipboard = () => {
    navigator.clipboard.writeText(orderCode).then(() => {
      toast({
        title: 'Code copié',
        description: 'Le code de commande a été copié dans le presse-papier',
        variant: 'default'
      });
    }).catch(err => {
      console.error('Erreur lors de la copie du code:', err);
      toast({
        title: 'Erreur',
        description: 'Impossible de copier le code',
        variant: 'destructive'
      });
    });
  };
  
  const sendConfirmationEmail = async () => {
    if (!customerEmail) {
      toast({
        title: 'Email manquant',
        description: 'Aucune adresse email trouvée pour envoyer la confirmation',
        variant: 'destructive'
      });
      return;
    }
    
    setIsSendingEmail(true);
    
    try {
      // Récupérer les données du panier depuis localStorage (si disponibles)
      const cartData = localStorage.getItem('last_order_items');
      const orderItems = cartData ? JSON.parse(cartData) : [];
      
      // Récupérer l'ID de la commande stocké lors de la vérification
      const orderId = localStorage.getItem('current_order_id');
      
      if (!orderId) {
        throw new Error('ID de commande non trouvé');
      }
      
      const result = await sendOrderConfirmationEmail(orderId);
      
      if (result.success) {
        setEmailSent(true);
        toast({
          title: 'Email envoyé',
          description: 'La confirmation de commande a été envoyée à votre adresse email',
          variant: 'default'
        });
      } else {
        throw new Error(result.error || 'Erreur lors de l\'envoi de l\'email');
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi de l\'email de confirmation:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible d\'envoyer l\'email de confirmation',
        variant: 'destructive'
      });
    } finally {
      setIsSendingEmail(false);
    }
  };
  
  if (isLoading) {
    return (
      <PublicLayout>
        <div className="min-h-screen relative overflow-hidden">
          {/* Fond avec la nouvelle palette de couleurs SIS */}
          <div 
            className="fixed inset-0 z-0 bg-center bg-no-repeat bg-cover"
            style={{
              backgroundImage: tenant?.config?.bg ? `url(${tenant.config.bg})` : 'url()',
              backgroundAttachment: 'fixed'
            }}
          >
            {shopTheme.overlay.enabled && (
              <div className="absolute inset-0 backdrop-blur-[2px]" style={{
                backgroundImage: shopTheme.overlay.useGradient
                  ? `linear-gradient(${(
                      {
                        'to-b': 'to bottom',
                        'to-t': 'to top',
                        'to-r': 'to right',
                        'to-l': 'to left',
                        'to-tr': 'to top right',
                        'to-br': 'to bottom right',
                        'to-tl': 'to top left',
                        'to-bl': 'to bottom left',
                      } as Record<string, string>
                    )[shopTheme.overlay.orientation] || 'to bottom'},
                      ${hexToRgba(shopTheme.overlay.color1, shopTheme.overlay.opacity1)},
                      ${hexToRgba(shopTheme.overlay.color2, shopTheme.overlay.opacity2)},
                      ${hexToRgba(shopTheme.overlay.color3, shopTheme.overlay.opacity3)}
                    )`
                  : undefined,
                backgroundColor: !shopTheme.overlay.useGradient
                  ? hexToRgba(shopTheme.overlay.color1, shopTheme.overlay.opacity2)
                  : undefined,
              }} />
            )}
          </div>

          <div className="relative z-10 flex flex-col justify-center items-center h-screen">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2" style={{ borderColor: tenant?.config?.color1 || '#fe8d17' }} />
            <span style={{ color: tenant?.config?.color4 || '#9ca3af' }}>Chargement de la confirmation…</span>
          </div>
        </div>
      </PublicLayout>
    );
  }

  if (!orderExists) {
    return null; // Un toast + redirection ont déjà été effectués
  }

  return (
    <PublicLayout>
      <SEOMetaTags 
        title={seoConfig.title} 
        description={seoConfig.description}
        image={seoConfig.image}
        url={seoConfig.url}
      />

      <div className="text-white relative overflow-hidden min-h-screen">
        {/* Fond avec la nouvelle palette de couleurs SIS */}
        <div 
          className="fixed inset-0 z-0 bg-center bg-no-repeat bg-cover"
          style={{
            backgroundImage: tenant?.config?.bg ? `url(${tenant.config.bg})` : 'url()',
            backgroundAttachment: 'fixed'
          }}
        >
          {shopTheme.overlay.enabled && (
            <div className="absolute inset-0 backdrop-blur-[2px]" style={{
              backgroundImage: shopTheme.overlay.useGradient
                ? `linear-gradient(${(
                    {
                      'to-b': 'to bottom',
                      'to-t': 'to top',
                      'to-r': 'to right',
                      'to-l': 'to left',
                      'to-tr': 'to top right',
                      'to-br': 'to bottom right',
                      'to-tl': 'to top left',
                      'to-bl': 'to bottom left',
                    } as Record<string, string>
                  )[shopTheme.overlay.orientation] || 'to bottom'},
                    ${hexToRgba(shopTheme.overlay.color1, shopTheme.overlay.opacity1)},
                    ${hexToRgba(shopTheme.overlay.color2, shopTheme.overlay.opacity2)},
                    ${hexToRgba(shopTheme.overlay.color3, shopTheme.overlay.opacity3)}
                  )`
                : undefined,
              backgroundColor: !shopTheme.overlay.useGradient
                ? hexToRgba(shopTheme.overlay.color1, shopTheme.overlay.opacity2)
                : undefined,
            }} />
          )}
        </div>

        {/* Particules flottantes avec couleurs et icônes personnalisables */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Top-Left */}
          {shopTheme.icons[0]?.enabled && (
            <motion.div
              className="absolute top-20 left-10"
              style={{ color: hexToRgba(shopTheme.icons[0].color, 0.2) }}
              animate={{ y: [-20, 20, -20], rotate: [0, 180, 360] }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            >
              {(() => {
                const IconComponent = iconMap[shopTheme.icons[0].icon] || iconMap.CheckCircle;
                return IconComponent ? <IconComponent size={shopTheme.icons[0].size} /> : null;
              })()}
            </motion.div>
          )}
          {/* Top-Right */}
          {shopTheme.icons[1]?.enabled && (
            <motion.div
              className="absolute top-40 right-20"
              style={{ color: hexToRgba(shopTheme.icons[1].color, 0.2) }}
              animate={{ y: [20, -20, 20], rotate: [360, 180, 0] }}
              transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
            >
              {(() => {
                const IconComponent = iconMap[shopTheme.icons[1].icon] || iconMap.Sparkles;
                return IconComponent ? <IconComponent size={shopTheme.icons[1].size} /> : null;
              })()}
            </motion.div>
          )}
          {/* Bottom-Left */}
          {shopTheme.icons[2]?.enabled && (
            <motion.div
              className="absolute bottom-40 left-20"
              style={{ color: hexToRgba(shopTheme.icons[2].color, 0.2) }}
              animate={{ y: [-15, 15, -15], rotate: [0, 90, 180] }}
              transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
            >
              {(() => {
                const IconComponent = iconMap[shopTheme.icons[2].icon] || iconMap.Diamond;
                return IconComponent ? <IconComponent size={shopTheme.icons[2].size} /> : null;
              })()}
            </motion.div>
          )}
          {/* Bottom-Right */}
          {shopTheme.icons[3]?.enabled && (
            <motion.div
              className="absolute bottom-20 right-10"
              style={{ color: hexToRgba(shopTheme.icons[3].color, 0.2) }}
              animate={{ y: [15, -15, 15], rotate: [180, 270, 360] }}
              transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
            >
              {(() => {
                const IconComponent = iconMap[shopTheme.icons[3].icon] || iconMap.Star;
                return IconComponent ? <IconComponent size={shopTheme.icons[3].size} /> : null;
              })()}
            </motion.div>
          )}
        </div>

        <div className="relative z-10 container mx-auto px-4 py-12">
          <motion.div 
            className="space-y-8"
            initial="hidden"
            animate="visible"
            variants={fadeIn}
          >
            <Card className="backdrop-blur-sm text-white"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderColor: `${tenant?.config?.color1 || '#fe8d17'}33`
              }}
            >
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <CheckCircle className="h-16 w-16" style={{ color: '#10b981' }} />
              </div>
              <CardTitle className="text-3xl" style={{ color: tenant?.config?.color3 || '#ffffff' }}>Commande confirmée</CardTitle>
              <CardDescription style={{ color: tenant?.config?.color4 || '#9ca3af' }}>
                Merci pour votre commande ! Votre commande a été traitée avec succès.
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-6">
              <div className="p-6 rounded-lg"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  borderColor: `${tenant?.config?.color1 || '#fe8d17'}33`
                }}
              >
                <h3 className="text-xl font-semibold mb-2" style={{ color: tenant?.config?.color4 || '#ffffff' }}>Détails de la commande</h3>
                <div className="flex items-center justify-between p-4 rounded-md"
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderColor: `${tenant?.config?.color1 || '#fe8d17'}4d`
                  }}
                >
                  <div>
                    <p style={{ color: tenant?.config?.color4 || '#9ca3af' }}>Code de commande</p>
                    <p className="text-xl font-mono" style={{ color: tenant?.config?.color3 || '#ffffff' }}>{orderCode}</p>
                  </div>
                  <Button variant="outline" size="icon" className="transition-colors"
                    style={{
                      borderColor: tenant?.config?.color1 || '#fe8d17',
                      color: tenant?.config?.color1 || '#fe8d17'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = `${tenant?.config?.color1 || '#fe8d17'}1a`;
                      e.currentTarget.style.borderColor = tenant?.config?.color1 || '#fe8d17';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.borderColor = tenant?.config?.color1 || '#fe8d17';
                    }}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                
                <div className="mt-4">
                  <p style={{ color: tenant?.config?.color3 || '#9ca3af' }} className="mb-2">Conservez ce code pour suivre votre commande</p>
                  <p style={{ color: tenant?.config?.color3 || '#9ca3af' }}>Vous pouvez accéder à tout moment aux détails de votre commande en utilisant ce code ou votre adresse email.</p>
                </div>
              </div>
              
              <div className="p-6 rounded-lg"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  borderColor: `${tenant?.config?.color1 || '#fe8d17'}33`
                }}
              >
                <h3 className="text-xl font-semibold mb-4" style={{ color: tenant?.config?.color4 || '#ffffff' }}>Que se passe-t-il maintenant ?</h3>
                <ol className="space-y-3 list-decimal list-inside" style={{ color: tenant?.config?.color3 || '#9ca3af' }}>
                  <li>Vous recevrez un email de confirmation avec les détails de votre commande</li>
                  <li>Votre commande sera préparée et expédiée dans les plus brefs délais</li>
                  <li>Vous recevrez des mises à jour sur l'état de votre commande par email</li>
                  <li>Vous pouvez suivre votre commande à tout moment en utilisant le code ci-dessus</li>
                </ol>
              </div>
            </CardContent>
            
            <CardFooter className="flex-col space-y-4">
              {customerEmail && !emailSent ? (
                <Button 
                  onClick={sendConfirmationEmail} 
                  disabled={isSendingEmail}
                  className="w-full backdrop-blur-sm transition-all duration-300"
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderColor: `${tenant?.config?.color1 || '#fe8d17'}4d`,
                    color: tenant?.config?.color3 || '#ffffff'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                  }}
                >
                  <Mail className="mr-2 h-4 w-4" />
                  {isSendingEmail ? 'Envoi en cours...' : 'Envoyer la confirmation par email'}
                </Button>
              ) : emailSent ? (
                <div className="w-full p-3 rounded-md flex items-center justify-center"
                  style={{
                    backgroundColor: 'rgba(16, 185, 129, 0.2)',
                    color: '#10b981'
                  }}
                >
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Email de confirmation envoyé
                </div>
              ) : null}
              
              <div className="flex space-x-4 w-full">
                <Button 
                  onClick={() => navigate('/boutique')} 
                  variant="outline" 
                  className="flex-1 transition-all duration-300"
                  style={{
                    borderColor: `${tenant?.config?.color1 || '#fe8d17'}4d`,
                    color: tenant?.config?.color1 || '#fe8d17'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = `${tenant?.config?.color1 || '#fe8d17'}1a`;
                    e.currentTarget.style.borderColor = tenant?.config?.color1 || '#fe8d17';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.borderColor = `${tenant?.config?.color1 || '#fe8d17'}4d`;
                  }}
                >
                  Retour à la boutique
                </Button>
                <Button 
                  onClick={() => navigate('/boutique/commandes')} 
                  className="flex-1 shadow-lg hover:shadow-xl transition-all duration-300 font-bold"
                  style={{
                    backgroundColor: tenant?.config?.color1 || '#fe8d17',
                    color: tenant?.config?.color3 || '#ffffff',
                    backgroundImage: `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`,
                    borderColor: `${tenant?.config?.color1 || '#fe8d17'}4d`,
                    boxShadow: `0 4px 15px ${hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.4)}`
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#e07a10'}, ${tenant?.config?.color1 || '#fe8d17'})`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`;
                  }}
                >
                  Voir mes commandes
                </Button>
              </div>
            </CardFooter>
          </Card>
          </motion.div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default OrderConfirmationPage;
